/**
 * Recovering from a selected account that is not available here.
 *
 * The selected account comes from `?acc=` or local storage and was never
 * checked, so an id that does not exist on this environment (a production
 * link opened on dev), a deleted account, or one the user no longer belongs to
 * left the app stuck: the account's location list answered 404 or 403 and the
 * page showed "Unable to load locations" until site data was cleared.
 *
 * The server's answer is the test, not a client-side list: 404 (no such
 * account) and 403 (not yours) mean the selection is stale; anything else
 * (500, network) is a real failure and is still reported.
 */

import { isResourceDeepLink } from './postLoginRedirect';

/** True when an error means "this account is not available to you here". */
export function isUnavailableAccountError(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 404 || status === 403;
}

/**
 * The account to fall back to: the first candidate that is set, is a real
 * account id (not "ALL"), and is not one already found unavailable. Null when
 * there is none — the caller then clears the selection.
 */
export function pickFallbackAccount(candidates: Array<string | null | undefined>, unavailable: ReadonlySet<string>): string | null {
  for (const id of candidates) {
    if (id && id !== 'ALL' && !unavailable.has(id)) return id;
  }
  return null;
}

/**
 * Whether to say "That account isn't available to you here" when moving off
 * an unavailable account. Not for an address naming a work order, note or
 * invoice (an emailed link to another shop's work): that screen already says
 * "You can't open this …" and why, and a second message only confuses.
 */
export function announceUnavailableAccount(search: string): boolean {
  return !isResourceDeepLink(new URLSearchParams(search.replace(/^[?&]/, '')));
}
